/**
 * RIDE88 — Force Variant Selection
 * --------------------------------------------------------------------------
 * Requires customers to consciously select every variant option before the
 * "Add to Cart" button becomes active, on:
 *   - the main product page
 *   - the quick view drawer
 *   - embedded blocks like the Featured Product section
 *
 * IMPORTANT: This file is intentionally SEPARATE from product-info.js so it
 * SURVIVES THEME UPDATES. Theme updates reset vendor files (product-info.js)
 * but do not touch files the vendor never created (this one) or theme.liquid.
 * It is loaded from theme.liquid via a <script> tag — do NOT paste this into
 * product-info.js, or it will be wiped on the next theme update.
 *
 * HOW IT HANDLES DROPDOWNS vs SWATCHES:
 *   - Native <select> dropdowns always have SOMETHING selected, so to create a
 *     true "nothing chosen" state we inject a disabled "Choose ..." placeholder
 *     option and keep it selected. This also keeps a `selected` attribute on the
 *     <select> at all times, which the theme's variant-selects.js REQUIRES — it
 *     calls `.find(option[selected]).removeAttribute(...)` on change and would
 *     throw (breaking Add to Cart) if no option had the attribute.
 *   - Radio/swatch inputs support a genuine "nothing checked" state, so we just
 *     uncheck them.
 *
 * Re-enabling the button is left to the theme's own updateButtonsState logic,
 * which runs once a real variant resolves. We only force it to STAY disabled
 * while any option group is still untouched.
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var PLACEHOLDER_ATTR = 'data-force-placeholder';
  var PLACEHOLDER_TEXT = 'Select an option';

  // Snapshot whether the page originally loaded with ?variant=... in the URL,
  // before the theme starts rewriting the URL on option changes.
  var originalUrlHasVariant = new URLSearchParams(window.location.search).has('variant');

  function initForceVariantSelection(info) {
    if (info._forceSelectInitialized) return;
    info._forceSelectInitialized = true;

    // Context. Embedded blocks (Featured Product) carry data-update-url="false";
    // quick view drawers carry data-view-mode="quick-view". Only the MAIN product
    // page should honor the URL's variant parameter.
    var syncsWithUrl = info.dataset.updateUrl !== 'false';
    var isQuickView = info.dataset.viewMode === 'quick-view';
    var isMainProduct = syncsWithUrl && !isQuickView;

    if (originalUrlHasVariant && isMainProduct) return;

    var variantScript = info.querySelector('script[type="application/json"]');
    if (!variantScript) return;

    var variants = [];
    try {
      variants = JSON.parse(variantScript.textContent);
    } catch (e) {
      return;
    }

    // Only run when the product has MORE than one variant.
    if (!variants || variants.length <= 1) return;

    var initialVS = info.querySelector('variant-selects');
    var optionGroups = (initialVS || info).querySelectorAll('.product-form__input');
    var totalOptionGroups = optionGroups.length;
    if (totalOptionGroups < 1) return;

    var selectedGroups = new Set();
    var observer = null;
    var suppressObserver = false;

    // ---- Helpers --------------------------------------------------------

    function disableAddToCart() {
      var btn = info.querySelector('button[name="add"]');
      if (btn) {
        // Only write when state actually differs — redundant writes force
        // needless style recalculations.
        if (!btn.hasAttribute('disabled')) {
          btn.setAttribute('disabled', 'disabled');
          btn.style.pointerEvents = 'none';
          btn.style.opacity = '0.6';
        }
        var btnText = btn.querySelector('span');
        if (btnText && btnText.textContent !== 'Select all options') {
          btnText.textContent = 'Select all options';
        }
      }
      var stickyButtons = document.querySelectorAll('.sticky-atc-bar [name="add"]');
      for (var i = 0; i < stickyButtons.length; i++) {
        var b = stickyButtons[i];
        if (!b.hasAttribute('disabled')) {
          b.setAttribute('disabled', 'disabled');
          b.style.pointerEvents = 'none';
          b.style.opacity = '0.6';
        }
      }
    }

    // Clear a single option group (used for groups the user hasn't touched).
    function clearGroup(group) {
      // --- Dropdowns: inject/keep a selected placeholder option ---
      var selects = group.querySelectorAll('select');
      for (var s = 0; s < selects.length; s++) {
        var select = selects[s];
        var placeholder = select.querySelector('option[' + PLACEHOLDER_ATTR + ']');
        if (!placeholder) {
          placeholder = document.createElement('option');
          placeholder.setAttribute(PLACEHOLDER_ATTR, '');
          placeholder.setAttribute('disabled', 'disabled');
          placeholder.value = '';
          placeholder.textContent = PLACEHOLDER_TEXT;
          select.insertBefore(placeholder, select.firstChild);
        }
        // If the placeholder is already the active selection, this group is
        // already cleared — skip the per-option work entirely.
        if (select.selectedIndex === 0 && placeholder.hasAttribute('selected')) {
          continue;
        }
        // Remove `selected` from every real option, keep it on the placeholder
        // (the theme requires exactly one option to carry `selected`).
        var opts = select.options;
        for (var o = 0; o < opts.length; o++) {
          if (opts[o] !== placeholder) opts[o].removeAttribute('selected');
        }
        placeholder.setAttribute('selected', 'selected');
        select.selectedIndex = 0;
      }

      // --- Radios / swatches: uncheck everything ---
      var radios = group.querySelectorAll('input[type="radio"]:checked');
      for (var r = 0; r < radios.length; r++) {
        radios[r].checked = false;
        radios[r].removeAttribute('checked');
      }

      // --- Selected-value text label (e.g. "Pin Size: 15mm") ---
      var label = group.querySelector('[data-selected-swatch-value]');
      if (label && label.textContent !== '') label.textContent = '';
    }

    // The theme replaces the entire <variant-selects> node on each change
    // (see HTMLUpdateUtility.viewTransition in theme.js: it inserts a NEW node,
    // hides the old one, and removes it ~500ms later). During that overlap BOTH
    // nodes are in the DOM, so we must always resolve and query the CURRENT
    // (newest, visible) one rather than querying broadly from product-info,
    // which would return duplicate option groups from the stale node.
    function currentVariantSelects() {
      return info.querySelector('variant-selects'); // newest is first in document order
    }

    function getGroups() {
      var vs = currentVariantSelects();
      return (vs || info).querySelectorAll('.product-form__input');
    }

    function clearUnselectedGroups() {
      var groups = getGroups();
      for (var i = 0; i < groups.length; i++) {
        if (!selectedGroups.has(i)) clearGroup(groups[i]);
      }
    }

    // Run our DOM mutations without re-triggering the observer.
    function withObserverSuppressed(fn) {
      suppressObserver = true;
      try {
        fn();
      } finally {
        // Release on the next microtask so the mutations we just made are
        // flushed while still suppressed.
        Promise.resolve().then(function () {
          suppressObserver = false;
        });
      }
    }

    // ---- Initial state --------------------------------------------------

    info.currentVariant = null;
    info.lastKnownGoodVariant = null;

    withObserverSuppressed(function () {
      clearUnselectedGroups();
      disableAddToCart();
    });

    // ---- Watch for the theme re-rendering the option HTML ---------------
    // CRITICAL: the theme does NOT mutate <variant-selects> in place — it
    // REPLACES the whole node (insert new sibling, hide+remove the old). So we
    // must observe a STABLE ancestor that survives the swap, not the node
    // itself. We watch the parent of variant-selects; when the new node is
    // inserted (a childList change on the parent), the observer fires and we
    // re-clear the untouched groups in the new node. This fires AFTER the swap
    // — and after the theme's own updateButtonsState — so re-disabling wins.
    //
    // PERFORMANCE: childList/subtree only (NOT attributes, which would fire on
    // every class/style toggle during the transition). All mutations from a
    // single render are coalesced into ONE cleanup pass via requestAnimationFrame.
    var variantSelects = currentVariantSelects();
    var observeTarget = variantSelects && variantSelects.parentNode ? variantSelects.parentNode : info;
    if (observeTarget) {
      var cleanupScheduled = false;
      observer = new MutationObserver(function () {
        if (suppressObserver || cleanupScheduled) return;
        if (selectedGroups.size >= totalOptionGroups) return; // all chosen: leave it
        cleanupScheduled = true;
        requestAnimationFrame(function () {
          cleanupScheduled = false;
          if (selectedGroups.size >= totalOptionGroups) return;
          withObserverSuppressed(function () {
            clearUnselectedGroups();
            disableAddToCart();
          });
        });
      });
      observer.observe(observeTarget, { childList: true, subtree: true });
    }

    // ---- Track which groups the customer actually interacts with --------
    info.addEventListener('change', function (event) {
      var group = event.target.closest('.product-form__input');
      if (!group) return;
      // Scope the index lookup to the variant-selects that owns the event,
      // so a stale node lingering during the transition can't shift indices.
      var scope = event.target.closest('variant-selects') || currentVariantSelects() || info;
      var groups = scope.querySelectorAll('.product-form__input');
      var index = Array.prototype.indexOf.call(groups, group);
      if (index !== -1) selectedGroups.add(index);
    });
  }

  // Patch the custom element so EVERY instance runs our logic on connect.
  function patchProductInfo() {
    var ctor = customElements.get('product-info');
    if (!ctor) return false;

    var proto = ctor.prototype;
    if (proto._forceSelectPatched) return true;
    proto._forceSelectPatched = true;

    var originalConnected = proto.connectedCallback;
    proto.connectedCallback = function () {
      if (typeof originalConnected === 'function') {
        originalConnected.apply(this, arguments);
      }
      var self = this;
      requestAnimationFrame(function () {
        initForceVariantSelection(self);
      });
    };

    // Handle any product-info elements already connected before this ran.
    document.querySelectorAll('product-info').forEach(initForceVariantSelection);
    return true;
  }

  if (window.customElements) {
    if (!patchProductInfo()) {
      customElements.whenDefined('product-info').then(patchProductInfo);
    }
  }
})();
